import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { TourKeys } from '@cast/constants';

import { apiClient } from '@cast/infrastructure-api';
import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';

const CLUSTER_DASHBOARD_RESOURCE_USAGE_QUERY_KEY =
  'cluster-dashboard-resource-usage';

const getResourceUsageQueryKey = ({
  clusterId,
  startTime,
  endTime,
  stepSeconds,
  tourStepId,
}: {
  clusterId: string;
  startTime: string;
  endTime: string;
  stepSeconds?: number;
  tourStepId: string | null;
}) =>
  [
    CLUSTER_DASHBOARD_RESOURCE_USAGE_QUERY_KEY,
    clusterId,
    startTime,
    endTime,
    stepSeconds,
    tourStepId,
  ] as const;

type ResourceUsageQueryParameters = {
  clusterId: string;
  startTime: string;
  endTime: string;
  stepSeconds?: number;
  enabled?: boolean;
  refetchInterval?: (data: unknown) => number | false;
};

const getResourceUsageQueryOptions = (
  {
    clusterId,
    startTime,
    endTime,
    stepSeconds,
    enabled = true,
    refetchInterval,
  }: ResourceUsageQueryParameters,
  tourStepId: string | null,
  client: ReturnType<typeof useQueryClient>
) =>
  queryOptions({
    queryKey: getResourceUsageQueryKey({
      clusterId,
      startTime,
      endTime,
      stepSeconds,
      tourStepId,
    }),
    queryFn: async () => {
      return apiClient.costReport
        .getResourceUsage({ clusterId, startTime, endTime, stepSeconds })
        .then((resp) => {
          return resp.data?.items || [];
        });
    },
    enabled,
    refetchInterval: ({ queryKey }) => {
      if (refetchInterval) {
        return refetchInterval(client.getQueryData(queryKey));
      }
      return false;
    },
  });

export const useResourceUsageQuery = (
  parameters: ResourceUsageQueryParameters
) => {
  const client = useQueryClient();
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);

  const {
    data: resourceUsage,
    isLoading,
    error,
    refetch,
  } = useQuery(getResourceUsageQueryOptions(parameters, tourStepId, client));

  return {
    resourceUsage: resourceUsage || undefined,
    isLoading,
    error,
    refetch,
  };
};
